import { ref, computed, onBeforeUnmount, useHost, type CSSProperties } from "vue";
import type { ToasterProps, ToastData, ToastPosition } from "./toast.types";
import { toasterClasses } from "./toast.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import {
  toast,
  TOAST_CREATE_EVENT,
  TOAST_DISMISS_EVENT,
  TOAST_PAUSE_EVENT,
  TOAST_RESUME_EVENT,
  isValidToastData,
} from "./toast.api";
import { parseBoolAttr } from "@/runtime/shared/attrs";
import { useSharedState } from "composables/useSharedState";

export interface UseToasterOptions {
  props: Readonly<ToasterProps>;
}

const MAX_TOASTS = 20;

// Module-level dedup: prevents multiple toasters from handling the same toast event.
const handledIds = new Set<string>();

const enrichToastData = (data: ToastData, props: Readonly<ToasterProps>): ToastData => {
  return {
    ...data,
    duration: data.duration ?? (Number(props.duration) || 4000),
    closeButton: data.closeButton ?? (parseBoolAttr(props.closeButton, false) ? true : undefined),
    richColors: data.richColors ?? parseBoolAttr(props.richColors, false),
    count: 1,
  };
};

const toastKey = (d: ToastData): string => {
  return `${d.type}:${d.title}:${d.description ?? ""}`;
};

const useToastList = (props: Readonly<ToasterProps>) => {
  const toasts = ref<ToastData[]>([]);

  const add = (data: ToastData): void => {
    const enriched = enrichToastData(data, props);
    const shouldDedup = parseBoolAttr(props.dedup, false);
    const key = toastKey(enriched);
    const existingIndex = shouldDedup ? toasts.value.findIndex((t) => toastKey(t) === key) : -1;

    if (existingIndex !== -1) {
      const existing = toasts.value[existingIndex];
      const updated = { ...existing, count: (existing.count ?? 1) + 1 };
      const without = toasts.value.filter((_, i) => i !== existingIndex);
      toasts.value = [...without, updated];
      return;
    }

    const current = toasts.value.length >= MAX_TOASTS ? toasts.value.slice(1) : toasts.value;
    toasts.value = [...current, enriched];
  };

  const remove = (id: string): void => {
    toasts.value = toasts.value.filter((t) => t.id !== id);
  };

  const clear = (): void => {
    toasts.value = [];
  };

  return { toasts, add, remove, clear };
};

const computeToastStyle = (
  index: number,
  total: number,
  visible: number,
  gapPx: number,
  expanded: boolean,
  position: ToastPosition | undefined,
): CSSProperties => {
  if (expanded) {
    return index < total - 1 ? { marginBottom: `${gapPx}px` } : {};
  }
  if (index >= total - 1) return {};

  const depth = total - 1 - index;
  const bottom = (position ?? "bottom-right").startsWith("bottom");

  return {
    position: "absolute",
    left: "0",
    right: "0",
    ...(bottom ? { top: `${-depth * 8}px` } : { bottom: `${-depth * 8}px` }),
    transform: `scale(${1 - depth * 0.05})`,
    transformOrigin: bottom ? "bottom center" : "top center",
    zIndex: -depth,
    opacity: index >= total - visible ? 1 : 0,
  };
};

const useToasterEvents = (list: ReturnType<typeof useToastList>): void => {
  const handleCreate = (event: Event): void => {
    const data = (event as CustomEvent<ToastData>).detail;
    if (!isValidToastData(data) || handledIds.has(data.id)) return;
    handledIds.add(data.id);
    setTimeout(() => handledIds.delete(data.id), 100);
    event.stopImmediatePropagation();
    list.add(data);
  };

  const handleDismiss = (event: Event): void => {
    event.stopImmediatePropagation();
    const { id } = (event as CustomEvent<{ id?: string }>).detail ?? {};
    if (id) {
      list.remove(id);
    } else {
      list.clear();
    }
  };

  // Register immediately during setup — not in onMounted — so toasts dispatched
  // before the component finishes mounting are not lost.
  document.addEventListener(TOAST_CREATE_EVENT, handleCreate);
  document.addEventListener(TOAST_DISMISS_EVENT, handleDismiss);

  onBeforeUnmount(() => {
    document.removeEventListener(TOAST_CREATE_EVENT, handleCreate);
    document.removeEventListener(TOAST_DISMISS_EVENT, handleDismiss);
  });
};

export const useToaster = (options: UseToasterOptions) => {
  const { props } = options;
  const host = useHost();
  useSharedState(props);
  if (host) (host as any).toast = toast;

  const expanded = ref(false);
  const list = useToastList(props);
  useToasterEvents(list);

  const classes = computed(() => mergeClassProp(toasterClasses({ position: props.position }), props.class));
  const listClasses = "pointer-events-auto flex flex-col";
  const visibleCount = computed(() => Math.max(1, Number(props.visibleToasts) || 3));
  const gap = computed(() => Number(props.gap) || 14);
  const isExpanded = computed(() => parseBoolAttr(props.expand, false) || expanded.value);

  return {
    classes,
    listClasses,
    toasts: list.toasts,
    isExpanded,
    handleToastEvent: (event: Event): void => {
      const detail = (event as CustomEvent<{ id: string }>).detail;
      if (detail?.id) list.remove(detail.id);
    },
    handleMouseEnter: (): void => {
      expanded.value = true;
      document.dispatchEvent(new CustomEvent(TOAST_PAUSE_EVENT));
    },
    handleMouseLeave: (): void => {
      expanded.value = false;
      document.dispatchEvent(new CustomEvent(TOAST_RESUME_EVENT));
    },
    handleDismissAll: list.clear,
    toastStyle: (index: number): CSSProperties =>
      computeToastStyle(
        index,
        list.toasts.value.length,
        visibleCount.value,
        gap.value,
        isExpanded.value,
        props.position,
      ),
    toastApi: toast,
  };
};
